/**
 * DocumentPreparation — dynamic upload step driven by ProposedDocuments.
 *
 * The borrower sees the same dynamic doc list Owen has been showing in the URLA
 * right rail: rebuilt from product_document_conditions + their stated information.
 * Each item gets a per-document upload button. A "Coming Soon: Digital Verification"
 * banner sets expectations for Phase 2 (Plaid / Truework / Argyle).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Surface } from '../../ui/components';
import {
  ArrowLeft, ArrowRight, Sparkles, Zap, ShieldCheck,
} from 'lucide-react';
import { setCurrentStep, addDocument, STEPS } from '../../store/loanApplicationSlice';
import { LK_FEATURES } from '../../config/features';
import ApplyLayout from '../layout/ApplyLayout';
import ProposedDocuments from './ProposedDocuments';
import LiveReadinessScore from './LiveReadinessScore';
import V1DocumentPrep from './v1/V1DocumentPrep';

const DocumentPreparation = () => {
  const dispatch = useDispatch();
  const applicationData = useSelector((s) => s.loanApplication.applicationData);
  const recommendedProduct = useSelector((s) => s.loanApplication.recommendedProduct);
  const v1 = useSelector((s) => s.loanApplication.v1);
  const documents = useSelector((s) => s.loanApplication.documents) || [];

  const [uploadedMap, setUploadedMap] = useState({});
  const fileInputRef = useRef(null);
  const [pendingDocName, setPendingDocName] = useState(null);

  // Hydrate from existing redux documents.
  useEffect(() => {
    const next = {};
    documents.forEach((d) => {
      if (d?.docName) next[d.docName] = true;
    });
    setUploadedMap(next);
  }, [documents]);

  const handleUploadClick = (docName) => {
    setPendingDocName(docName);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingDocName) return;
    dispatch(addDocument({
      docName: pendingDocName,
      fileName: file.name,
      fileSize: file.size,
      uploadedAt: new Date().toISOString(),
    }));
    setUploadedMap((prev) => ({ ...prev, [pendingDocName]: true }));
    setPendingDocName(null);
    e.target.value = '';
  };

  const handleBack = () => dispatch(setCurrentStep(STEPS.APPLICATION));
  const handleContinue = () => {
    // MVP: skip the AI-vs-human picker and go straight to Owen's verdict.
    dispatch(setCurrentStep(LK_FEATURES.humanUnderwriter ? STEPS.UNDERWRITING_PICKER : STEPS.UNDERWRITING));
  };

  const uploadedCount = Object.values(uploadedMap).filter(Boolean).length;

  const rightRail = useMemo(() => (
    <LiveReadinessScore />
  ), []);

  if (LK_FEATURES.v1ProductMatrix && v1?.productId) {
    return (
      <V1DocumentPrep
        productId={v1.productId}
        triageAnswers={v1.triageAnswers}
        fieldAnswers={v1.fieldAnswers}
        onBack={handleBack}
        onContinue={handleContinue}
      />
    );
  }

  return (
    <ApplyLayout
      step={3}
      totalSteps={5}
      title="Documents to Prove It"
      subtitle="Owen built this list from what you told us. Upload now, or skip and forward your file as-is — your loan officer can collect docs on the closing path."
      context="documents"
      rightRailContent={rightRail}
    >
      {/* Hidden file input for upload-on-demand */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.heic,.webp"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Optional-step framing */}
      <Surface
        padding="var(--lk-space-4)"
        style={{
          marginBottom: 'var(--lk-space-5)',
          backgroundColor: 'var(--lk-primary-50)',
          border: '1px solid var(--lk-primary-200)',
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}
      >
        <Sparkles size={18} color="var(--lk-primary-600)" style={{ marginTop: 2, flexShrink: 0 }} />
        <div>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            This step is optional — but uploading docs makes Owen's underwriting verdict much sharper.
          </div>
          <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-secondary)' }}>
            {uploadedCount > 0
              ? `You've uploaded ${uploadedCount} document${uploadedCount === 1 ? '' : 's'} so far. Each one boosts your Mortgage Readiness Score and gives Owen more to verify against.`
              : 'Upload now and Owen verifies stated values against the actual documents during underwriting. Or skip and Owen runs against your stated values only.'}
          </div>
        </div>
      </Surface>

      {/* The dynamic, full-mode document panel */}
      <ProposedDocuments
        applicationData={applicationData}
        productId={recommendedProduct?.id}
        compact={false}
        uploadedDocs={uploadedMap}
        onUploadClick={handleUploadClick}
      />

      {/* Coming Soon: Digital Verification */}
      <Surface
        padding="var(--lk-space-4) var(--lk-space-5)"
        style={{
          marginTop: 'var(--lk-space-5)',
          border: '1px dashed var(--lk-border)',
          backgroundColor: 'var(--lk-bg-surface-2)',
          display: 'flex', gap: 14, alignItems: 'flex-start',
        }}
      >
        <Zap size={20} color="var(--lk-warning-500)" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
          }}>
            <span style={{ fontWeight: 700 }}>Coming Soon: Digital Verification</span>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 8px',
              borderRadius: 4, backgroundColor: 'var(--lk-warning-100)',
              color: 'var(--lk-warning-700)', textTransform: 'uppercase',
            }}>
              Phase 2
            </span>
          </div>
          <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-secondary)' }}>
            Skip the document hunt: connect your bank, employer, and tax accounts to verify
            assets, income, and employment instantly via Plaid, Truework, and Argyle.
          </div>
          {LK_FEATURES.digitalVerification && (
            <Button variant="secondary" size="sm" style={{ marginTop: 10 }}>
              Set up digital verification
            </Button>
          )}
        </div>
      </Surface>

      {/* Reassurance */}
      <div style={{
        marginTop: 'var(--lk-space-5)', padding: 'var(--lk-space-3) var(--lk-space-4)',
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)',
      }}>
        <ShieldCheck size={14} />
        <span>Bank-level encryption · Documents are only shared if you choose to forward your file.</span>
      </div>

      {/* Action bar */}
      <div style={{
        display: 'flex', gap: 'var(--lk-space-3)',
        marginTop: 'var(--lk-space-8)',
      }}>
        <Button variant="secondary" size="lg" icon={ArrowLeft} onClick={handleBack}>
          Back to Application
        </Button>
        <Button variant="primary" size="lg" icon={ArrowRight} onClick={handleContinue} style={{ flex: 1 }}>
          {uploadedCount > 0
            ? "Continue to Owen's Verdict"
            : 'Skip Upload & Continue'}
        </Button>
      </div>
    </ApplyLayout>
  );
};

export default DocumentPreparation;
